<template>
  <div class="rounded-lg border p-2 dark:border-yapdarkgray-600 dark:bg-yapdarkgray-900">
    <div
      id="drop"
      ref="dropZoneRef"
      class="flex w-full flex-col items-center justify-center gap-2 rounded-lg border p-2 py-4 text-sm dark:border-yapdarkgray-500"
      :class="{
        'bg-primary-200 dark:bg-primary-dark-800': isOverDropZone,
        'bg-white dark:bg-yapdarkgray-800': !isOverDropZone,
        'border-yapred-400 dark:border-red-500': state.hasErrors,
        'opacity-50': state.maxNbFilesReached
      }"
    >
      <button
        :disabled="state.maxNbFilesReached"
        :class="{ 'cursor-pointer': allowFileDialog && !state.maxNbFilesReached }"
        class="w-full"
        @click="openDialog"
      >
        <div class="flex w-full items-center justify-between">
          <YapIcon
            class="mr-2 h-6 min-h-6 w-6 min-w-6 text-yapgray-800 dark:text-yapgray-200"
            name="document-text-outline"
          />

          <div class="flex w-full items-baseline gap-2 font-semibold text-yapgray-800 dark:text-yapgray-200">
            {{ dropzoneText }}
          </div>
        </div>
      </button>
    </div>
    <div>
      <span
        v-if="instructionsText"
        class="whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-bold text-yapgray-700 dark:text-yapgray-100"
        >{{ instructionsText }}</span
      >
    </div>

    <ul
      v-if="!!state.fileResources.length"
      class="mt-2 divide-y rounded-lg border dark:divide-yapdarkgray-500 dark:border-yapdarkgray-600 dark:text-yapgray-100"
    >
      <li
        v-for="(fileResource, index) in state.fileResources"
        :key="fileResource.resourceUid"
        class="flex items-center justify-between p-1 pl-2"
        :class="{ 'text-red-500': !!fileResource.error }"
      >
        <button class="grid w-full cursor-pointer" @click="handlePreviewClick(fileResource)">
          <div class="flex items-baseline gap-2">
            <span>{{ trimFilename(fileResource.displayName) }}</span>
            <span class="text-xs" v-if="fileResource.fileSize">{{
              bytesToHumanReadableString(fileResource.fileSize)
            }}</span>
          </div>
          <pre v-if="!!fileResource.error" class="text-xs text-red-500">{{ fileResource.error }}</pre>
        </button>

        <EditFileResourceNameModal
          v-if="fileResource.resourceUid"
          :file-resource="fileResource"
          @update="onFileResourceNameUpdated"
        />
        <YapFileResourceDownloadButton :file-resource="fileResource" />
        <YapTooltip :text="t('dict.label.delete')">
          <YapButtonIcon :loading="fileResource.loading" @click="deleteFile(index)">
            <YapIcon name="trash-outline" class="h-6 w-6 text-yapred-500 dark:text-yapred-400" />
          </YapButtonIcon>
        </YapTooltip>
      </li>
    </ul>
    <YapFileResourceModalPreview
      v-if="!!state.selectedFileForPreview"
      :set-is-open="!!state.selectedFileForPreview"
      :file="state.selectedFileForPreview"
      @modal-closed="state.selectedFileForPreview = undefined"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useDropZone, useFileDialog } from '@vueuse/core';

import apiGw from '@/services/apiGw';
import { useI18n } from '@/plugins/i18n';
import { fileToBase64 } from '@/utils/file';
import YapIcon from '@/components/YapIcon.vue';
import useHelper from '@/composables/useHelper';
import { FileResource } from '@/types/fileResource';
import YapTooltip from '@/components/YapTooltip.vue';
import YapButtonIcon from '@/components/YapButtonIcon.vue';
import { useContextSwitchStore } from '@/store/contextSwitchStore';
import { FileData } from '@/components/transactions/types/receiptFile';
import EditFileResourceNameModal from '@/components/fileResource/EditFileResourceNameModal.vue';
import YapFileResourceModalPreview from '@/components/fileResource/YapFileResourceModalPreview.vue';
import YapFileResourceDownloadButton from '@/components/fileResource/YapFileResourceDownloadButton.vue';

const emit = defineEmits<{
  uploaded: [
    Array<{
      bucketUid: string;
      displayName: string;
      fileSize: number;
      mimeType: string;
      uid: string;
    }>
  ];
  deleted: [string];
  renamed: [{ resourceUid: string; newName: string }];
}>();

const props = withDefaults(
  defineProps<{
    dropzoneText: string;
    instructionsText?: string;
    accept?: string;
    multipleFiles?: boolean;
    allowFileDialog?: boolean;
    fileResources?: FileResource[];
    maxFiles?: number;
  }>(),
  {
    instructionsText: undefined,
    accept: '*',
    multipleFiles: true,
    allowFileDialog: true,
    fileResources: undefined,
    maxFiles: Infinity
  }
);

const dropZoneRef = ref<HTMLDivElement>();

const { t } = useI18n();
const contextSwitchStore = useContextSwitchStore();
const { bytesToHumanReadableString } = useHelper();
const { isOverDropZone } = useDropZone(dropZoneRef, onDrop);
const { files, open, reset } = useFileDialog({ multiple: props.multipleFiles, reset: true, accept: props.accept });

interface State {
  hasErrors: boolean;
  resetting: boolean;
  fileResources: Array<FileResource & { loading?: boolean; error?: any }>;
  selectedFileForPreview?: FileData;
  maxNbFilesReached: boolean;
}
const state: State = reactive({
  hasErrors: false,
  resetting: false,
  fileResources: props.fileResources ?? [],
  selectedFileForPreview: undefined,
  maxNbFilesReached: computed(() => state.fileResources.length >= props.maxFiles)
});

function openDialog() {
  resetDialog();
  if (props.allowFileDialog) {
    open();
  }
}

function onDrop(files: File[] | null) {
  if (files) {
    updloadFiles(files);
  }
}

watch(files, (selectedFiles: FileList | null) => {
  if (selectedFiles) {
    updloadFiles(Array.from(selectedFiles));
  }
});

function deleteFile(index: number) {
  const fileToDelete = state.fileResources[index];
  if (fileToDelete.resourceUid) {
    apiGw.delete(`/v2/file-resources/${fileToDelete.resourceUid}`);
    emit('deleted', fileToDelete.resourceUid);
  }
  state.fileResources.splice(index, 1);
}

function resetDialog() {
  reset();
}

async function updloadFiles(files: File[]) {
  const uploadedFileResources: Array<{
    bucketUid: string;
    displayName: string;
    fileSize: number;
    mimeType: string;
    uid: string;
  }> = [];
  if (state.maxNbFilesReached || state.fileResources.length + files.length >= props.maxFiles) {
    alert(
      t('webapp.payments.internal_note.receipt.max_file_reached', [
        props.maxFiles,
        props.maxFiles - state.fileResources.length
      ])
    );
    return;
  }
  await Promise.all(
    files.map(async (file, idx) => {
      const fileResource: FileResource & { loading: boolean } = {
        displayName: file.name,
        mimeType: file.type,
        fileSize: file.size,
        loading: true,
        resourceUid: crypto.randomUUID(),
        bucketFileName: 'n/a'
      };
      state.fileResources.push(fileResource);

      const b64 = await fileToBase64(file);

      try {
        const rsp = await apiGw.post(`/v2/file-resources/base64`, {
          content: b64,
          displayName: file.name,
          mimeType: file.type,
          ownerUid: contextSwitchStore.getCurrentContext()?.uid
        });
        const fr = state.fileResources.find((file) => file.resourceUid === fileResource.resourceUid);
        if (fr) {
          fr.loading = false;
          fr.resourceUid = rsp.data.uid;
          fr.bucketFileName = rsp.data.bucketUid;
        }
        uploadedFileResources.push({
          bucketUid: rsp.data.bucketUid,
          displayName: rsp.data.displayName,
          fileSize: rsp.data.fileSize,
          mimeType: rsp.data.mimeType,
          uid: rsp.data.uid
        });
      } catch (error: any) {
        console.error('Error uploading files: ', error);
        const err = error.response?.data;
        const fr = state.fileResources.find((file) => file.resourceUid === fileResource.resourceUid);
        if (fr) {
          fr.loading = false;
          fr.error = `${err.details}`;
        }
      }
    })
  );

  emit('uploaded', uploadedFileResources);
}

function onFileResourceNameUpdated(data: { resourceUid: string; newName: string }) {
  const fileResource = state.fileResources.find((file) => file.resourceUid === data.resourceUid);
  if (fileResource) {
    fileResource.displayName = data.newName;
    emit('renamed', data);
  }
}

function trimFilename(filename: string) {
  const maxLength = 25;
  const extension = filename.split('.').pop();
  if (filename.length > maxLength && extension) {
    let name = filename.slice(0, filename.length - extension.length - 1);
    if (name.length > maxLength) {
      name = name.slice(0, maxLength) + '...' + extension;
    } else {
      name = name + '.' + extension;
    }
    return name;
  } else {
    return filename;
  }
}

function handlePreviewClick(fileResource: FileResource) {
  state.selectedFileForPreview = {
    fileName: fileResource.bucketFileName ?? fileResource.bucketUid ?? '',
    originalName: fileResource.displayName,
    mime: fileResource.mimeType,
    resourceUid: fileResource.resourceUid ?? fileResource.uid ?? ''
  };
}
</script>

<style scoped>
#drop {
  box-shadow:
    inset 0 2px 2px hsla(0, 0%, 0%, 0.1),
    0 2px 0 hsla(0, 0%, 100%, 0.082);
}
</style>
